import React, { useEffect, useState } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

interface CreateDonorSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scope: 'donors' | 'prospects'
}

type DonorType = 'individual' | 'organization'

type DonorStatus = 'prospect' | 'active' | 'inactive'

interface IndividualForm {
  individual_donor: {
    first_name: string
    last_name: string
    email: string
    phone: string
    status: DonorStatus
    address_line1: string
    address_line2: string
    city: string
    postal_code: string
    country: string
  }
}

interface OrganizationForm {
  organization_donor: {
    organization_name: string
    email: string
    phone: string
    status: DonorStatus
    address_line1: string
    address_line2: string
    city: string
    postal_code: string
    country: string
    tax_id: string
    legal_form: string
    sector: string
    website: string
  }
}

function defaultIndividual(status: DonorStatus): IndividualForm {
  return {
    individual_donor: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      status,
      address_line1: '',
      address_line2: '',
      city: '',
      postal_code: '',
      country: '',
    },
  }
}

function defaultOrganization(status: DonorStatus): OrganizationForm {
  return {
    organization_donor: {
      organization_name: '',
      email: '',
      phone: '',
      status,
      address_line1: '',
      address_line2: '',
      city: '',
      postal_code: '',
      country: '',
      tax_id: '',
      legal_form: '',
      sector: '',
      website: '',
    },
  }
}

export function CreateDonorSheet({ open, onOpenChange, scope }: CreateDonorSheetProps) {
  const [donorType, setDonorType] = useState<DonorType>('individual')
  const [addressExpanded, setAddressExpanded] = useState(false)

  const defaultStatus: DonorStatus = scope === 'prospects' ? 'prospect' : 'active'
  const individualForm = useForm<IndividualForm>(defaultIndividual(defaultStatus))
  const organizationForm = useForm<OrganizationForm>(defaultOrganization(defaultStatus))

  useEffect(() => {
    individualForm.reset()
    organizationForm.reset()
    setAddressExpanded(false)
  }, [donorType])

  function handleClose() {
    individualForm.reset()
    organizationForm.reset()
    setAddressExpanded(false)
    setDonorType('individual')
    onOpenChange(false)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (donorType === 'individual') {
      individualForm.post('/people/individual_donors', {
        onSuccess: () => {
          individualForm.reset()
          onOpenChange(false)
        },
      })
    } else {
      organizationForm.post('/people/organization_donors', {
        onSuccess: () => {
          organizationForm.reset()
          onOpenChange(false)
        },
      })
    }
  }

  const processing = donorType === 'individual' ? individualForm.processing : organizationForm.processing

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {scope === 'prospects' ? 'Nouveau prospect' : 'Nouveau donnateur'}
          </SheetTitle>
          <SheetDescription>
            {scope === 'prospects'
              ? 'Creez un nouveau prospect individuel ou une organisation prospect.'
              : 'Creez un nouveau donnateur individuel ou une organisation donatrice.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex gap-2 p-4 pb-0">
          <Button
            type="button"
            variant={donorType === 'individual' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDonorType('individual')}
          >
            Individuel
          </Button>
          <Button
            type="button"
            variant={donorType === 'organization' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDonorType('organization')}
          >
            Organisation
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {donorType === 'individual' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="first_name">Prenom *</Label>
                <Input
                  id="first_name"
                  value={individualForm.data.individual_donor.first_name}
                  onChange={(e) =>
                    individualForm.setData('individual_donor', {
                      ...individualForm.data.individual_donor,
                      first_name: e.target.value,
                    })
                  }
                  required
                  placeholder="Prenom"
                  autoFocus
                />
                {individualForm.errors['individual_donor.first_name'] && (
                  <p className="text-sm text-destructive">
                    {individualForm.errors['individual_donor.first_name']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  value={individualForm.data.individual_donor.last_name}
                  onChange={(e) =>
                    individualForm.setData('individual_donor', {
                      ...individualForm.data.individual_donor,
                      last_name: e.target.value,
                    })
                  }
                  required
                  placeholder="Nom de famille"
                />
                {individualForm.errors['individual_donor.last_name'] && (
                  <p className="text-sm text-destructive">
                    {individualForm.errors['individual_donor.last_name']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ind_email">Email</Label>
                <Input
                  id="ind_email"
                  type="email"
                  value={individualForm.data.individual_donor.email}
                  onChange={(e) =>
                    individualForm.setData('individual_donor', {
                      ...individualForm.data.individual_donor,
                      email: e.target.value,
                    })
                  }
                  placeholder="email@exemple.com"
                />
                {individualForm.errors['individual_donor.email'] && (
                  <p className="text-sm text-destructive">
                    {individualForm.errors['individual_donor.email']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ind_phone">Telephone</Label>
                <Input
                  id="ind_phone"
                  value={individualForm.data.individual_donor.phone}
                  onChange={(e) =>
                    individualForm.setData('individual_donor', {
                      ...individualForm.data.individual_donor,
                      phone: e.target.value,
                    })
                  }
                  placeholder="+216 XX XXX XXX"
                />
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setAddressExpanded(!addressExpanded)}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <span>{addressExpanded ? '▼' : '▶'}</span>
                  <span>Adresse</span>
                </button>
                {addressExpanded && (
                  <div className="space-y-3 mt-3">
                    <div className="space-y-2">
                      <Label htmlFor="ind_address1">Adresse ligne 1</Label>
                      <Input
                        id="ind_address1"
                        value={individualForm.data.individual_donor.address_line1}
                        onChange={(e) =>
                          individualForm.setData('individual_donor', {
                            ...individualForm.data.individual_donor,
                            address_line1: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ind_address2">Adresse ligne 2</Label>
                      <Input
                        id="ind_address2"
                        value={individualForm.data.individual_donor.address_line2}
                        onChange={(e) =>
                          individualForm.setData('individual_donor', {
                            ...individualForm.data.individual_donor,
                            address_line2: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="ind_city">Ville</Label>
                        <Input
                          id="ind_city"
                          value={individualForm.data.individual_donor.city}
                          onChange={(e) =>
                            individualForm.setData('individual_donor', {
                              ...individualForm.data.individual_donor,
                              city: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ind_postal">Code postal</Label>
                        <Input
                          id="ind_postal"
                          value={individualForm.data.individual_donor.postal_code}
                          onChange={(e) =>
                            individualForm.setData('individual_donor', {
                              ...individualForm.data.individual_donor,
                              postal_code: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ind_country">Pays (code ISO)</Label>
                      <Input
                        id="ind_country"
                        value={individualForm.data.individual_donor.country}
                        onChange={(e) =>
                          individualForm.setData('individual_donor', {
                            ...individualForm.data.individual_donor,
                            country: e.target.value.toUpperCase().slice(0, 2),
                          })
                        }
                        maxLength={2}
                        placeholder="TN"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="organization_name">Nom de l&apos;organisation *</Label>
                <Input
                  id="organization_name"
                  value={organizationForm.data.organization_donor.organization_name}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      organization_name: e.target.value,
                    })
                  }
                  required
                  placeholder="Nom de l'organisation"
                  autoFocus
                />
                {organizationForm.errors['organization_donor.organization_name'] && (
                  <p className="text-sm text-destructive">
                    {organizationForm.errors['organization_donor.organization_name']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="org_email">Email</Label>
                <Input
                  id="org_email"
                  type="email"
                  value={organizationForm.data.organization_donor.email}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      email: e.target.value,
                    })
                  }
                  placeholder="contact@organisation.com"
                />
                {organizationForm.errors['organization_donor.email'] && (
                  <p className="text-sm text-destructive">
                    {organizationForm.errors['organization_donor.email']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="org_phone">Telephone</Label>
                <Input
                  id="org_phone"
                  value={organizationForm.data.organization_donor.phone}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      phone: e.target.value,
                    })
                  }
                  placeholder="+216 XX XXX XXX"
                />
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setAddressExpanded(!addressExpanded)}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <span>{addressExpanded ? '▼' : '▶'}</span>
                  <span>Adresse</span>
                </button>
                {addressExpanded && (
                  <div className="space-y-3 mt-3">
                    <div className="space-y-2">
                      <Label htmlFor="org_address1">Adresse ligne 1</Label>
                      <Input
                        id="org_address1"
                        value={organizationForm.data.organization_donor.address_line1}
                        onChange={(e) =>
                          organizationForm.setData('organization_donor', {
                            ...organizationForm.data.organization_donor,
                            address_line1: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="org_address2">Adresse ligne 2</Label>
                      <Input
                        id="org_address2"
                        value={organizationForm.data.organization_donor.address_line2}
                        onChange={(e) =>
                          organizationForm.setData('organization_donor', {
                            ...organizationForm.data.organization_donor,
                            address_line2: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="org_city">Ville</Label>
                        <Input
                          id="org_city"
                          value={organizationForm.data.organization_donor.city}
                          onChange={(e) =>
                            organizationForm.setData('organization_donor', {
                              ...organizationForm.data.organization_donor,
                              city: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="org_postal">Code postal</Label>
                        <Input
                          id="org_postal"
                          value={organizationForm.data.organization_donor.postal_code}
                          onChange={(e) =>
                            organizationForm.setData('organization_donor', {
                              ...organizationForm.data.organization_donor,
                              postal_code: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="org_country">Pays (code ISO)</Label>
                      <Input
                        id="org_country"
                        value={organizationForm.data.organization_donor.country}
                        onChange={(e) =>
                          organizationForm.setData('organization_donor', {
                            ...organizationForm.data.organization_donor,
                            country: e.target.value.toUpperCase().slice(0, 2),
                          })
                        }
                        maxLength={2}
                        placeholder="TN"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_id">Numero fiscal</Label>
                <Input
                  id="tax_id"
                  value={organizationForm.data.organization_donor.tax_id}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      tax_id: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="legal_form">Forme juridique</Label>
                <Input
                  id="legal_form"
                  value={organizationForm.data.organization_donor.legal_form}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      legal_form: e.target.value,
                    })
                  }
                  placeholder="Association, SARL, SA..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sector">Secteur</Label>
                <Input
                  id="sector"
                  value={organizationForm.data.organization_donor.sector}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      sector: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Site web</Label>
                <Input
                  id="website"
                  type="url"
                  value={organizationForm.data.organization_donor.website}
                  onChange={(e) =>
                    organizationForm.setData('organization_donor', {
                      ...organizationForm.data.organization_donor,
                      website: e.target.value,
                    })
                  }
                  placeholder="https://www.exemple.com"
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing} className="flex-1">
              {processing ? 'Creation...' : 'Creer'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
